import React from 'react';
import { Link } from 'react-router-dom';
import { goalTypeToUrl } from '../utils/goalTypeUtils';
import { indicatorMatchesGoal } from '../utils/indicatorGoalMatch';
import { getGoalDisplayTitle } from '../utils/goalDisplayTitle';
import { stripLeadingIndicatorCode } from '../utils/displayText';
import SDGSpinner from './SDGSpinner';
import GoalAssessmentBadges from './GoalAssessmentBadges';
import './HomeIndicatorsList.css';

/**
 * Home “All indicators” section — title, catalog link, and indicators
 * grouped by goal. Shared by desktop and mobile home layouts.
 */
const HomeIndicatorsList = ({
  goals,
  goalType,
  indicators,
  indicatorsLoading,
  copy = {},
  handleIndicatorClick,
  variant = 'desktop',
}) => {
  const title =
    copy.indicatorsSectionTitle ||
    (goalType === 'eu' ? 'EU SDG Indicators' : 'UN SDG Indicators');
  const subtitle =
    copy.indicatorsSectionSubtitle || 'Click the indicator to view the latest data';
  const catalogLink =
    copy.viewAllIndicatorsLink ||
    'View all indicators (including those without data yet)';
  const emptyMessage =
    copy.noIndicatorsMessage ||
    `No indicators available for ${goalType === 'eu' ? 'EU' : 'UN'} SDGs`;

  const headingId = 'home-indicators-list-heading';
  const isMobile = variant === 'mobile';

  return (
    <section
      className={`home-indicators-list home-indicators-list--${variant}`}
      aria-labelledby={headingId}
    >
      <header className="home-indicators-list__intro">
        <h2 id={headingId} className="home-indicators-list__title">
          {title}
        </h2>
        <p className="home-indicators-list__subtitle">{subtitle}</p>
        <Link
          to={`/${goalTypeToUrl(goalType)}/indicators-catalog`}
          className="home-indicators-list__catalog-link"
        >
          {catalogLink}
          <i className="bi bi-arrow-right home-indicators-list__catalog-arrow" aria-hidden="true" />
        </Link>
      </header>

      <div className="home-indicators-list__body">
        {indicatorsLoading && (!indicators || indicators.length === 0) && (
          <div className="home-indicators-list__state">
            <SDGSpinner text={isMobile ? 'Loading indicators...' : 'Loading all indicators'} />
          </div>
        )}

        {!indicatorsLoading && indicators.length === 0 && (
          <div className="home-indicators-list__state home-indicators-list__state--empty">
            {emptyMessage}
          </div>
        )}

        {!indicatorsLoading &&
          indicators.length > 0 &&
          goals.map((goal) => {
            const goalIndicators = indicators.filter((indicator) =>
              indicatorMatchesGoal(indicator, goal, goalType)
            );
            if (!goalIndicators.length) return null;

            return (
              <div key={goal.id} className="home-indicators-list__goal-group">
                <h3 className="home-indicators-list__goal-title">
                  {getGoalDisplayTitle(goal)}
                </h3>
                <ul className="home-indicators-list__items">
                  {goalIndicators.map((indicator) => (
                    <li key={indicator.id} className="home-indicators-list__item">
                      <button
                        type="button"
                        className="home-indicators-list__item-btn"
                        onClick={() => handleIndicatorClick(goal.id, indicator.id)}
                      >
                        <GoalAssessmentBadges
                          goalType={goalType}
                          statusCategory={indicator?.computed?.semaphore_status}
                          trendCategory={indicator?.computed?.trend}
                          size="sm"
                          variant="plain"
                        />
                        <span className="home-indicators-list__item-label">
                          {indicator.acf?.index ? (
                            <strong>{indicator.acf.index}</strong>
                          ) : null}{' '}
                          {stripLeadingIndicatorCode(
                            indicator.title_render || indicator.title,
                            indicator.acf?.index
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
      </div>
    </section>
  );
};

export default HomeIndicatorsList;
